import { addTimesheet, deleteTimesheet } from "firebaseService/firebaseService";
import { CalendarPlus, Check, Clock, ShieldCheck, Trash2, X, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { LEAVE_TYPES } from "utils/types";
import { formatDate, getNSWHolidays } from "utils/utils";

export const TimesheetGrid = ({ employees, leaveRequests, payPeriod, currentUser, holidays, setHolidays, timesheetApprovals, onApproveTimesheet, onOpenAddLeaveModal, onOpenDeleteLeaveModal, onUpdateEmployee, loadingLeaveBalances = false }) => {
    const [searchTerm, setSearchTerm] = useState('');

    const calendarData = useMemo(() => {
        const days = [];
        let currentDate = new Date(payPeriod.start);

        while (currentDate <= payPeriod.end) {
            const dayCopy = new Date(currentDate);
            days.push({
                day: dayCopy.getDate(),
                monthName: dayCopy.toLocaleString('default', { month: 'short' }),
                date: dayCopy,
                isWeekend: dayCopy.getDay() === 0 || dayCopy.getDay() === 6,
            });

            currentDate = new Date(currentDate);
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return days;
    }, [payPeriod]);

    // Filter employees based on search term
    const filteredEmployees = useMemo(() => {
        if (!searchTerm.trim()) {
            return employees;
        }
        
        const searchLower = searchTerm.toLowerCase();
        return employees.filter(employee => 
            employee.name.toLowerCase().includes(searchLower) ||
            (employee.email && employee.email.toLowerCase().includes(searchLower))
        );
    }, [employees, searchTerm]);

    const isWeekend = (date) => {
        const dayOfWeek = date.getDay();
        return dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6
    };

    const isPublicHoliday = (date) => {
        const dateString = formatDate(date);
        // Check API holidays
        const isApiHoliday = holidays?.some(h => h.formattedDate === dateString && h.formattedDate !== "Not applicable");
        
        // Also check hardcoded holidays (Dec 25 & 26 for all years)
        const year = date.getFullYear();
        const month = date.getMonth() + 1; // getMonth() returns 0-11
        const day = date.getDate();
        const isHardcodedHoliday = (month === 12 && (day === 25 || day === 26));
        
        return isApiHoliday || isHardcodedHoliday;
    };

    const isWorkingDay = (date) => {
        return !isWeekend(date) && !isPublicHoliday(date);
    };

    const getLeaveForDay = (employeeId, date) => {
        // Don't show leave on weekends or public holidays
        if (!isWorkingDay(date)) {
            return null;
        }
        
        const dateString = formatDate(date);
        return leaveRequests.find(req => req.employeeId === employeeId && req.status === 'approved' && dateString >= req.startDate && dateString <= req.endDate);
    };

    const getPendingLeaveForDay = (employeeId, date) => {
        // Don't show leave on weekends or public holidays
        if (!isWorkingDay(date)) {
            return null;
        }
        
        const dateString = formatDate(date);
        return leaveRequests.find(req => req.employeeId === employeeId && req.status === 'pending' && dateString >= req.startDate && dateString <= req.endDate);
    };

    useEffect(() => {
        const fetchNSWHolidays = async () => {
            try {
                const holidays = await getNSWHolidays();
                if (holidays) {
                    setHolidays(holidays);
                } else {
                    console.error("Failed to fetch NSW holidays.");
                }
            } catch (error) {
                console.error("Error fetching NSW holidays:", error);
            }
        };
        // Fetch holidays when the component mounts
        fetchNSWHolidays();
    }, [setHolidays]);

    const onAddApproveTimesheet = async (employeeId, periodId) => {
        if (!employeeId || !periodId) {
            toast.error("Missing employee ID or pay period ID.");
            return;
        }
        try {
            await addTimesheet(employeeId, periodId);
            toast.success("Timesheet submitted successfully.");
        } catch (error) {
            console.error("Error submitting timesheet:", error);
            toast.error("Failed to submit timesheet. Please try again.");
        }
    }

    const onDeleteApproveTimesheet = async (employeeId, periodId) => {
        if (!employeeId || !periodId) {
            toast.error("Missing employee ID or pay period ID.");
            return;
        }
        try {
            await deleteTimesheet(employeeId, periodId);
            toast.success("Timesheet unsubmitted successfully and is now editable.");
        } catch (error) {
            console.error("Error submitting timesheet:", error);
            toast.error("Failed to submit timesheet. Please try again.");
        }
    }

    return (
        <div className="flex-grow bg-white rounded-lg shadow-md overflow-x-auto">
            <div className="min-w-[1490px]">
                {/* Search Bar */}
                <div className="p-4 bg-gray-50 border-b">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search employees by name or email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                    {searchTerm && (
                        <p className="text-sm text-gray-600 mt-2">
                            Showing {filteredEmployees.length} of {employees.length} employees
                        </p>
                    )}
                </div>

                <div className="grid grid-cols-[250px_1fr_100px_120px] sticky top-0 bg-white z-10 border-b">
                    <div className="p-4 font-semibold text-gray-700 border-r">Employee</div>
                    <div className="flex">
                        {calendarData.map(({ day, monthName, date }, index) => {
                            const dateStr = formatDate(date);
                            const holidayToday = holidays?.find(h => h.formattedDate === dateStr);
                            
                            // Check for hardcoded holidays (Dec 25 & 26)
                            const month = date.getMonth() + 1; // getMonth() returns 0-11
                            const dayOfMonth = date.getDate();
                            const isHardcodedHoliday = (month === 12 && (dayOfMonth === 25 || dayOfMonth === 26));
                            const isHoliday = holidayToday || isHardcodedHoliday;
                            
                            // Get holiday name for tooltip
                            let holidayName = holidayToday?.holiday;
                            if (isHardcodedHoliday && !holidayName) {
                                holidayName = dayOfMonth === 25 ? 'Christmas Day' : 'Boxing Day';
                            }

                            return (
                                <div
                                    key={index}
                                    className={`w-10 h-16 flex flex-col items-center justify-center border-l 
                                            ${date.getDay() === 0 || date.getDay() === 6 ? 'bg-gray-100' : ''}
                                            ${isHoliday ? 'bg-red-300' : ''}
                                        `}
                                    title={holidayName || undefined}
                                >
                                    {(day === 15 || day === 1) && (
                                        <span className="text-xs font-bold text-gray-500 absolute -mt-12">{monthName}</span>
                                    )}
                                    <span className="text-xs text-gray-400">{date.toString().split('')[0]}</span>
                                    <span className="text-sm font-medium text-gray-700">{day}</span>
                                </div>
                            );
                        })}
                    </div>
                    {currentUser.role !== 'employee' && (
                        <div className="p-3 font-medium text-gray-700 border-l border-b text-center">Approval</div>
                    )}
                    {currentUser.role === 'employee' && (
                        <div className="p-3 font-medium text-gray-700 border-l border-b text-center flex flex-col items-center justify-center">
                            <span>Submission</span>
                            <span className="text-[8px] text-gray-500 font-light mt-1">(End of pay cycle)</span>
                        </div>
                    )}
                </div>

                <div className="divide-y divide-gray-200">
                    {filteredEmployees.map(employee => {
                        const approval = timesheetApprovals.find(a => a.employeeId === employee.id && a.periodId === payPeriod.id);
                        const isApproved = approval?.status === 'approved';
                        const isSubmitted = approval?.status === 'pending';

                        return (
                            <div key={employee.id} className="grid grid-cols-[250px_1fr_100px_120px] items-center hover:bg-gray-50">
                                <div className="p-4 flex items-center gap-3 border-r h-full">
                                    <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center font-bold text-gray-600">{employee.initials || '??'}</div>
                                    <div>
                                        <p className="font-semibold text-gray-800">{employee.name}</p>
                                        <p className="text-sm text-gray-500">{employee.department}</p>
                                        <div className="flex items-center gap-2">
                                            <p className="text-xs text-blue-600 font-medium">
                                                Annual Leave: {
                                                    loadingLeaveBalances ? (
                                                        <span className="inline-flex items-center gap-1">
                                                            <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></span>
                                                            Loading...
                                                        </span>
                                                    ) : employee.annualLeaveHours !== undefined ? (
                                                        `${employee.annualLeaveHours.toFixed(2)} hours`
                                                    ) : (
                                                        'N/A'
                                                    )
                                                }
                                            </p>
                                            {currentUser.role === 'payroll' && (
                                              <></>  
                                            )}
                                        </div>
                                    </div>
                                    {(currentUser.role === 'manager' || currentUser.role === 'payroll') && (
                                        <div className="flex items-center gap-2 ml-auto">
                                            <button onClick={() => onOpenAddLeaveModal(employee)} className="text-gray-500 hover:text-blue-600 hover:bg-blue-100 rounded-full" title="Add leave for employee">
                                                <CalendarPlus size={16} />
                                            </button>
                                            <button onClick={() => onOpenDeleteLeaveModal(employee)} className="text-gray-500 hover:text-red-600 hover:bg-red-100 rounded-full" title="Delete leave for employee">
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div className="flex h-full">
                                    {calendarData.map(({ date }, index) => {
                                        const approvedLeave = getLeaveForDay(employee.id, date);
                                        const pendingLeave = getPendingLeaveForDay(employee.id, date);

                                        let cellClass = `w-10 h-full border-l`;
                                        let content = null;
                                        if (approvedLeave) {
                                            cellClass += ` ${LEAVE_TYPES[approvedLeave.leaveType]?.color || 'bg-gray-400'}`;
                                            content = <span className="text-white text-xs font-bold">{LEAVE_TYPES[approvedLeave.leaveType]?.abbreviation}</span>;
                                        } else if (pendingLeave) {
                                            cellClass += ` ${LEAVE_TYPES[pendingLeave.leaveType]?.color || 'bg-gray-400'} opacity-50`;
                                            content = <Clock size={18} className="text-white" />;
                                        } else {
                                            cellClass += ` ${date.getDay() === 0 || date.getDay() === 6 ? 'bg-gray-100' : 'bg-white'}`;
                                        }

                                        return (<div key={index} className={`${cellClass} flex items-center justify-center`}>{content}</div>);
                                    })}
                                </div>
                                {currentUser.role === 'manager' && employee.role === 'employee' && employee.id !== currentUser.id && (
                                    <div className="p-4 flex items-center justify-center border-l h-full gap-2">
                                        {isApproved ? (
                                            <div className="flex flex-col items-center text-green-600"><ShieldCheck size={20} /><span className="text-xs font-semibold mt-1">Approved</span></div>
                                        ) : (
                                            isSubmitted ? (
                                                <div className="flex items-center gap-2">
                                                    <button onClick={() => onApproveTimesheet(employee.id, payPeriod.id)} className="px-3 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm">Approve</button>
                                                    {employee.managerId && (
                                                        <button onClick={() => onDeleteApproveTimesheet(employee.id, payPeriod.id)} className="p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600" title="Unsubmit Timesheet">
                                                            <X size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                // If this employee has a manager, allow their manager to submit on their behalf
                                                employee.managerId ? (
                                                    <button onClick={() => onAddApproveTimesheet(employee.id, payPeriod.id)} className="px-3 py-1.5 bg-green-500 text-white rounded-md hover:bg-green-600 text-sm">Submit</button>
                                                ) : (
                                                    <button className="px-3 py-1.5 bg-blue-500 text-white rounded-md text-sm cursor-not-allowed opacity-60">Not submitted</button>
                                                )
                                            )
                                        )}
                                    </div>
                                )}
                                {currentUser.role === 'manager' && employee.role === 'manager' && employee.id !== currentUser.id && (
                                    <div className="p-4 flex items-center justify-center border-l h-full gap-2">
                                        {isApproved ? (
                                            <div className="flex flex-col items-center text-green-600"><ShieldCheck size={20} /><span className="text-xs font-semibold mt-1">Approved</span></div>
                                        ) : (
                                            isSubmitted ? (
                                                <div className="flex items-center gap-2">
                                                    <button onClick={() => onApproveTimesheet(employee.id, payPeriod.id)} className="px-3 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm">Approve</button>
                                                    {employee.managerId && (
                                                        <button onClick={() => onDeleteApproveTimesheet(employee.id, payPeriod.id)} className="p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600" title="Unsubmit Timesheet">
                                                            <X size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                // If this manager has a manager (is a line manager), allow their manager to submit on their behalf
                                                employee.managerId ? (
                                                    <button onClick={() => onAddApproveTimesheet(employee.id, payPeriod.id)} className="px-3 py-1.5 bg-green-500 text-white rounded-md hover:bg-green-600 text-sm">Submit</button>
                                                ) : (
                                                    <button className="px-3 py-1.5 bg-blue-500 text-white rounded-md text-sm cursor-not-allowed opacity-60">Not submitted</button>
                                                )
                                            )
                                        )}
                                    </div>
                                )}
                                {currentUser.role === 'payroll' && (
                                    <div className="p-4 flex items-center justify-center border-l h-full">
                                        {isApproved ? (
                                            <div className="flex flex-col items-center text-green-600"><ShieldCheck size={20} /><span className="text-xs font-semibold mt-1">Approved</span></div>
                                        ) : (
                                            isSubmitted ? (
                                                <div className="flex flex-col items-center text-blue-600"><Clock size={20} /><span className="text-xs font-semibold mt-1">Submitted</span></div>
                                            ) : (
                                                <div className="flex flex-col items-center text-gray-500"><Clock size={20} /><span className="text-xs font-semibold mt-1">Not submitted</span></div>
                                            )
                                        )}
                                    </div>
                                )}
                                {currentUser.role === 'employee' && (
                                    <div className="flex items-center justify-center border-l h-full gap-2">
                                        {isSubmitted || isApproved ? (
                                            <button
                                                onClick={() => onDeleteApproveTimesheet(currentUser.id, payPeriod.id)}
                                                className="p-2 bg-red-500 text-white rounded-full text-sm hover:bg-red-600 transition duration-150"
                                                title="Unsubmit Timesheet"
                                            >
                                                <X size={16} />
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => onAddApproveTimesheet(currentUser.id, payPeriod.id)}
                                                className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600"
                                                title="Submit"
                                            >
                                                <Check size={16} />
                                            </button>
                                        )}
                                    </div>
                                )}
                                {currentUser.role === 'manager' && employee.id === currentUser.id && (
                                    <div className="flex items-center justify-center border-l h-full gap-2">
                                        {isSubmitted || isApproved ? (
                                            <button
                                                onClick={() => onDeleteApproveTimesheet(currentUser.id, payPeriod.id)}
                                                className="p-2 bg-red-500 text-white rounded-full text-sm hover:bg-red-600 transition duration-150"
                                                title="Unsubmit Timesheet"
                                            >
                                                <X size={16} />
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => onAddApproveTimesheet(currentUser.id, payPeriod.id)}
                                                className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600"
                                                title="Submit"
                                            >
                                                <Check size={16} />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};